<script setup lang="ts">
import type { InfiniteData } from "@tanstack/vue-query";

import type { ExtendedEventsOverview, PaginatedList } from "~/types/Catalog";
import { EventListDisplayType } from "~/types/Event";

type Props = {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  displayType: EventListDisplayType;
  enableAutoscroll: boolean;
  enableNoEventsFoundMessage: boolean;
  trackingSource: string;
  enablePagination?: boolean;
  hasNextPage?: boolean;
  data?: InfiniteData<PaginatedList<ExtendedEventsOverview>>;
};

const {
  data,
  hasNextPage = false,
  enablePagination = true,
  displayType,
} = defineProps<Props>();

type Emits = {
  (e: "load-more"): void;
};

const emit = defineEmits<Emits>();

const allEvents = computed(
  () => data?.pages.flatMap((paginatedEvents) => paginatedEvents.results) ?? [],
);

const totalCount = computed(() => {
  return data?.pages[0]?.totalCount;
});

const onLoadMore = () => {
  emit("load-more");
};

const shouldShowCardView = computed(
  () =>
    displayType === EventListDisplayType.CardLarge ||
    displayType === EventListDisplayType.CardSmall,
);
</script>

<template>
  <div class="w-viewport px-0 event-list">
    <template v-if="isLoading">
      <EventCardGridSkeleton
        v-if="shouldShowCardView"
        :card-type="displayType"
      />
      <EventListSkeleton v-else />
    </template>
    <div
      v-else-if="isError"
      class="text-platform-danger p-4 md:p-0"
    >
      {{ $t("errors.loadingEvents") }}
    </div>
    <div v-else-if="isSuccess && data && allEvents?.length">
      <Suspense>
        <template #default>
          <LazyEventCardGrid
            v-if="shouldShowCardView"
            :card-type="displayType"
            :events="allEvents"
          />

          <div
            v-else
            class="mt-6"
          >
            <LazyEventProductsSlider
              v-for="(event, index) in allEvents"
              :key="event.id"
              :event-list-index="index"
              :tracking-source="trackingSource"
              :event="event"
              :enable-virtualization="enablePagination || enableAutoscroll"
            />
          </div>
        </template>
        <template #fallback>
          <EventCardGridSkeleton
            v-if="shouldShowCardView"
            :card-type="displayType"
          />
          <EventListSkeleton v-else />
        </template>
      </Suspense>

      <LazyBasePaginatorShowMore
        v-if="enablePagination && hasNextPage && totalCount"
        :amount-shown="allEvents.length"
        :total-count="totalCount"
        :enable-autoscroll="enableAutoscroll"
        :is-fetching="isFetching"
        @load-more="onLoadMore"
      />
    </div>
    <LazyBasePlaceholderNoResults
      v-else-if="isSuccess && !allEvents.length && enableNoEventsFoundMessage"
      class="px-4"
      variant="categoryPage"
    />
  </div>
</template>
